import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { createEntity, reset } from './grupo-empresarial.reducer';

export interface GrupoCrearFormValues {
  alias: string;
  grpAliasEquilibrio: string;
  grpAliasDecomsa: string;
}

const empty: GrupoCrearFormValues = {
  alias: '',
  grpAliasEquilibrio: '',
  grpAliasDecomsa: '',
};

const fields: FormField<GrupoCrearFormValues>[] = [
  { name: 'alias', label: 'Alias', required: true, placeholder: 'Alias del grupo' },
  { name: 'grpAliasEquilibrio', label: 'Alias equilibrio', placeholder: 'Alias equilibrio' },
  { name: 'grpAliasDecomsa', label: 'Alias decomsa', placeholder: 'Alias decomsa' },
];

const ACCENT = '#6366f1';

export interface GrupoCrearModalProps {
  open: boolean;
  onClose: () => void;
  /** Tras crear correctamente (p. ej. navegar al grupo). */
  onCreated?: (grupoId: number) => void;
}

export default function GrupoCrearModal({ open, onClose, onCreated }: GrupoCrearModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);
  const updateSuccess = useAppSelector(s => s.grupoEmpresarial.updateSuccess);
  const entity = useAppSelector(s => s.grupoEmpresarial.entity) as { id?: number } | null;

  const [form, setForm] = useState<GrupoCrearFormValues>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof GrupoCrearFormValues, string>>>({});
  /** Evita confundir updateSuccess/entity viejos (p. ej. tras visitar un grupo) con un alta recién hecha. */
  const createSubmittedRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) {
      createSubmittedRef.current = false;
      return;
    }
    createSubmittedRef.current = false;
    dispatch(reset());
    setForm(empty);
    setErrors({});
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) {
      createSubmittedRef.current = false;
      return;
    }
    if (!createSubmittedRef.current || updating) return;
    if (!updateSuccess || entity?.id == null) {
      createSubmittedRef.current = false;
      return;
    }
    const newId = entity.id;
    createSubmittedRef.current = false;
    dispatch(reset());
    if (onCreated) {
      onCreated(newId);
    } else {
      onClose();
    }
  }, [open, updating, updateSuccess, entity?.id, dispatch, onCreated, onClose]);

  const handleFieldChange = (name: keyof GrupoCrearFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof GrupoCrearFormValues, string>> = {};
    if (!form.alias.trim()) errs.alias = 'El alias es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    createSubmittedRef.current = true;
    dispatch(
      createEntity({
        alias: form.alias.trim(),
        grpAliasEquilibrio: form.grpAliasEquilibrio.trim(),
        grpAliasDecomsa: form.grpAliasDecomsa.trim(),
      }),
    );
  };

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal
        aria-labelledby="grupo-crear-modal-title"
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: '24px 28px',
          maxWidth: 520,
          width: '100%',
          maxHeight: 'min(90vh, 640px)',
          overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
        onMouseDown={e => e.stopPropagation()}
      >
        <h2
          id="grupo-crear-modal-title"
          style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}
        >
          Crear grupo empresarial
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
          Complete los datos del nuevo grupo.
        </p>
        <PageForm
          fields={fields}
          values={form}
          errors={errors}
          isNew
          saving={updating}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={onClose}
          accentColor={ACCENT}
          submitLabel="Crear grupo"
          cancelLabel="Cancelar"
        />
      </div>
    </div>,
    document.body,
  );
}
